// FILE: src/components/shell/DisplaySettings.jsx
// Display & accessibility settings (plan WP-8 tasks 5–6; MTO 5.2 "text
// resizable"): colour theme (device / light / dark), text size and reduced
// motion. Settings stay in this browser only. Used on the profile page and
// in the account menu's dialog.
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  Switch,
  ToggleButton,
  ToggleButtonGroup,
  Typography
} from "@mui/material";
import SettingsBrightnessOutlinedIcon from "@mui/icons-material/SettingsBrightnessOutlined";
import LightModeOutlinedIcon from "@mui/icons-material/LightModeOutlined";
import DarkModeOutlinedIcon from "@mui/icons-material/DarkModeOutlined";
import { useResolvedScheme } from "./ThemeToggle";
import { TEXT_SIZES, useDisplayPreferences } from "../../theme/preferences";

export function DisplaySettingsPanel() {
  const { mode, setMode } = useResolvedScheme();
  const { textSize, setTextSize, reduceMotion, setReduceMotion } = useDisplayPreferences();

  return (
    <Stack spacing={3}>
      <Box>
        <Typography id="theme-setting-label" sx={{ fontWeight: 650, mb: 1 }}>
          Colour theme
        </Typography>
        <ToggleButtonGroup
          exclusive
          value={mode}
          onChange={(_, v) => v && setMode(v)}
          aria-labelledby="theme-setting-label"
          sx={{ flexWrap: "wrap" }}
        >
          <ToggleButton value="system" aria-label="Same as my device">
            <SettingsBrightnessOutlinedIcon sx={{ mr: 1 }} aria-hidden /> Device
          </ToggleButton>
          <ToggleButton value="light" aria-label="Light">
            <LightModeOutlinedIcon sx={{ mr: 1 }} aria-hidden /> Light
          </ToggleButton>
          <ToggleButton value="dark" aria-label="Dark">
            <DarkModeOutlinedIcon sx={{ mr: 1 }} aria-hidden /> Dark
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      <Box>
        <Typography id="text-size-label" sx={{ fontWeight: 650, mb: 1 }}>
          Text size
        </Typography>
        <ToggleButtonGroup
          exclusive
          value={textSize}
          onChange={(_, v) => v && setTextSize(v)}
          aria-labelledby="text-size-label"
          sx={{ flexWrap: "wrap" }}
        >
          {TEXT_SIZES.map((s) => (
            <ToggleButton key={s.value} value={s.value} aria-label={`${s.label} text (${s.value}%)`}>
              <Box component="span" sx={{ fontSize: `${Number(s.value) / 100}rem` }}>
                {s.label}
              </Box>
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
        <Typography sx={{ color: "text.secondary", mt: 1 }}>
          Your browser's zoom (Ctrl/⌘ and +) works too.
        </Typography>
      </Box>

      <FormControlLabel
        control={<Switch checked={reduceMotion} onChange={(e) => setReduceMotion(e.target.checked)} />}
        label="Reduce motion (turn off animations)"
      />
      <Typography sx={{ color: "text.secondary" }}>
        These settings are saved in this browser only. Lesson content keeps its own look.
      </Typography>
    </Stack>
  );
}

export default function DisplaySettingsDialog({ open, onClose }) {
  return (
    <Dialog open={open} onClose={onClose} aria-labelledby="display-settings-title" fullWidth maxWidth="sm">
      <DialogTitle id="display-settings-title">Display &amp; accessibility</DialogTitle>
      <DialogContent>
        <DisplaySettingsPanel />
      </DialogContent>
      <DialogActions>
        <Button variant="contained" onClick={onClose}>
          Done
        </Button>
      </DialogActions>
    </Dialog>
  );
}
